import React, { useState, useEffect } from 'react';
import { FaEye, FaEdit, FaTrash, FaComment, FaSave } from 'react-icons/fa';

// Kanban Card Component
export const KanbanCard = ({ 
  lead, 
  onView, 
  onEdit, 
  onDelete, 
  onComment, 
  onStatusUpdate,
  onManagerAssignment,
  onAssociateAssignment,
  managers,
  assignedData,
  userId,
  selectedManager, // Pass selected manager from parent
  selectedAssociate, // Pass selected associate from parent
  onManagerChange, // Callback for manager change
  onAssociateChange // Callback for associate change
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [localManager, setLocalManager] = useState(selectedManager || '');
  const [localAssociate, setLocalAssociate] = useState(selectedAssociate || '');
  const [managerChanged, setManagerChanged] = useState(false);
  const [associateChanged, setAssociateChanged] = useState(false);

  // Sync with parent state changes
  useEffect(() => {
    setLocalManager(selectedManager || '');
    setLocalAssociate(selectedAssociate || '');
  }, [selectedManager, selectedAssociate]);

  const handleStatusChange = async (newStatus) => {
    if (lead.opp_status === newStatus) return;
    
    setIsUpdating(true);
    try {
      await onStatusUpdate(lead.id, newStatus);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleManagerChange = (newManagerId) => {
    setLocalManager(newManagerId);
    setManagerChanged(true);
    setLocalAssociate('');
    setAssociateChanged(false);
    onManagerChange(lead.id, newManagerId); // Notify parent
  };

  const handleAssociateChange = (newAssociateId) => {
    setLocalAssociate(newAssociateId);
    setAssociateChanged(true);
    onAssociateChange(lead.id, newAssociateId); // Notify parent
  };

  const handleSaveManager = () => {
    onManagerAssignment(lead.id, localManager);
    setManagerChanged(false);
  };

  const handleSaveAssociate = () => {
    onAssociateAssignment(lead.id, localAssociate);
    setAssociateChanged(false);
  };

  const statusColors = {
    'Prospecting': '#ffeb3b',
    'Proposal Sent': '#2196f3',
    'Negotiation': '#ff9800',
    'Regret': '#f44336',
    'Closed Won': '#4caf50',
    'Closed Lost': '#9e9e9e',
    'On Hold': '#607d8b'
  };

  const currentAssignment = assignedData[lead.id] || {};
  const manager = managers.find(m => m.id.toString() === localManager);
  const teamMembers = manager?.teamMembers || [];

  const getQuotationBadgeClass = (status) => {
    if (!status) return 'badge bg-light text-dark';
    
    switch (status.toLowerCase()) {
      case 'sent':
        return 'badge bg-success';
      case 'approved':
        return 'badge bg-secondary';
      case 'rejected':
        return 'badge bg-danger';
      case 'accepted':
        return 'badge bg-info';
      default:
        return 'badge bg-light text-dark';
    }
  };

  return (
    <div 
      className="kanban-card" 
      style={{ 
        opacity: isUpdating ? 0.6 : 1,
        cursor: 'grab'
      }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', lead.id.toString());
      }}
    >
      <div 
        className="kanban-card-header"
        style={{ backgroundColor: statusColors[lead.opp_status] || '#ccc' }}
      >
        <h6>Lead00{lead.id}</h6>
        <span className="kanban-status-badge">{lead.opp_status}</span>
      </div>
      <div className="kanban-card-body">
        <div className="kanban-field">
          <strong>Name:</strong> {lead.lead_name}
        </div>
        <div className="kanban-field">
          <strong>Contact:</strong> {lead.contact_number}
        </div>
        <div className="kanban-field">
          <strong>Email:</strong> {lead.email}
        </div>
        <div className="kanban-field">
          <strong>Source:</strong> {lead.lead_source}
        </div>
        <div className="kanban-field">
          <strong>Quotation:</strong> 
          <span className={getQuotationBadgeClass(lead.quotation_status)} style={{ fontSize: '0.7rem', padding: '0.25em 0.5em', marginLeft: '5px' }}>
            {lead.quotation_status || 'Not Quoted'}
          </span>
        </div>
        
        {/* Status Update */}
        <div className="kanban-field-group">
          <label><strong>Status:</strong></label>
          <select 
            className="form-select form-select-sm"
            value={lead.opp_status}
            onChange={(e) => handleStatusChange(e.target.value)}
            disabled={isUpdating}
          >
            <option value="Prospecting">Prospecting</option>
            <option value="Proposal Sent">Proposal Sent</option>
            <option value="Negotiation">Negotiation</option>
            <option value="Regret">Regret</option>
            <option value="Closed Won">Closed Won</option>
            <option value="Closed Lost">Closed Lost</option>
            <option value="On Hold">On Hold</option>
          </select>
        </div>

        {/* Manager Assignment */}
        <div className="kanban-field-group">
          <label><strong>Manager:</strong></label>
          <div className="d-flex gap-1">
            <select
              className="form-select form-select-sm"
              value={localManager}
              onChange={(e) => handleManagerChange(e.target.value)}
            >
              <option value="">Select Manager</option>
              <option value={userId}>
                Self {currentAssignment.manager_id === userId.toString() ? ' (Assigned)' : ''}
              </option>
              {managers.map((manager) => (
                <option
                  key={manager.id}
                  value={manager.id}
                  style={{
                    fontWeight: currentAssignment.manager_id === manager.id.toString() ? 'bold' : 'normal',
                  }}
                >
                  {manager.name}
                  {currentAssignment.manager_id === manager.id.toString() ? ' (Assigned)' : ''}
                </option>
              ))}
            </select>
            <button
              className="btn btn-sm btn-success"
              onClick={handleSaveManager}
              disabled={!managerChanged}
              title="Save Manager"
            >
              <FaSave />
            </button>
          </div>
        </div>

        {/* Associate Assignment */}
        <div className="kanban-field-group">
          <label><strong>Associate:</strong></label>
          <div className="d-flex gap-1">
            <select
              className="form-select form-select-sm"
              value={localAssociate}
              onChange={(e) => handleAssociateChange(e.target.value)}
              disabled={!localManager}
            >
              <option value="">Select Associate</option>
              {teamMembers.map((member) => (
                <option
                  key={member.id}
                  value={member.id}
                  style={{
                    fontWeight: currentAssignment.associate_id === member.id.toString() ? 'bold' : 'normal',
                  }}
                >
                  {member.name}
                  {currentAssignment.associate_id === member.id.toString() ? ' (Assigned)' : ''}
                </option>
              ))}
            </select>
            <button
              className="btn btn-sm btn-success"
              onClick={handleSaveAssociate}
              disabled={!associateChanged || !localManager}
              title="Save Associate"
            >
              <FaSave />
            </button>
          </div>
        </div>
        
        {/* Actions */}
        <div className="kanban-actions mt-2">
          <div className="d-flex gap-1 justify-content-center">
            <button className="btn btn-sm btn-outline-primary" onClick={() => onView(lead)} title="View">
              <FaEye />
            </button>
            {/* <button className="btn btn-sm btn-outline-primary" onClick={() => onEdit(lead)} title="Edit">
              <FaEdit />
            </button> */}
            <button className="btn btn-sm btn-outline-danger" onClick={() => onDelete(lead.id)} title="Delete">
              <FaTrash />
            </button>
            <button className="btn btn-sm btn-outline-primary" onClick={() => onComment(lead)} title="Comment">
              <FaComment />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component
export const KanbanColumn = ({ 
  title, 
  status, 
  leads, 
  onView, 
  onEdit, 
  onDelete, 
  onComment, 
  onStatusUpdate,
  onManagerAssignment,
  onAssociateAssignment,
  onDrop,
  managers,
  assignedData,
  userId,
  selectedManager, // Pass down selections
  selectedAssociate,
  onManagerChange,
  onAssociateChange
}) => {
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    const leadId = e.dataTransfer.getData('text/plain');
    if (leadId) {
      onDrop(parseInt(leadId), status);
    }
  };

  return (
    <div 
      className="kanban-column"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="kanban-column-header">
        <h6>{title}</h6>
        <span className="badge bg-secondary">{leads.length}</span>
      </div>
      <div className="kanban-column-body">
        {leads.map((lead) => (
          <KanbanCard 
            key={lead.id}
            lead={lead} 
            onView={onView}
           // onEdit={onEdit}
            onDelete={onDelete}
            onComment={onComment}
            onStatusUpdate={onStatusUpdate}
            onManagerAssignment={onManagerAssignment}
            onAssociateAssignment={onAssociateAssignment}
            managers={managers}
            assignedData={assignedData}
            userId={userId}
            selectedManager={selectedManager[lead.id]} // Pass specific lead's selection
            selectedAssociate={selectedAssociate[lead.id]}
            onManagerChange={onManagerChange}
            onAssociateChange={onAssociateChange}
          />
        ))}
        {leads.length === 0 && (
          <div className="kanban-empty-state">
            No leads in this status
          </div>
        )}
      </div>
    </div>
  );
};

// Kanban Board Component
export const KanbanBoard = ({ 
  leads, 
  onView, 
  onEdit, 
  onDelete, 
  onComment, 
  onStatusUpdate,
  onManagerAssignment,
  onAssociateAssignment,
  onDrop,
  managers,
  assignedData,
  userId,
  selectedManager, // Receive selections from parent
  selectedAssociate,
  onManagerChange, // Receive change handlers
  onAssociateChange
}) => {
  // Group leads by status
  const groupedLeads = {
    'Prospecting': leads.filter(lead => lead.opp_status === 'Prospecting'),
    'Proposal Sent': leads.filter(lead => lead.opp_status === 'Proposal Sent'),
    'Negotiation': leads.filter(lead => lead.opp_status === 'Negotiation'),
    'Regret': leads.filter(lead => lead.opp_status === 'Regret'),
    'Closed Won': leads.filter(lead => lead.opp_status === 'Closed Won'),
    'Closed Lost': leads.filter(lead => lead.opp_status === 'Closed Lost'),
    'On Hold': leads.filter(lead => lead.opp_status === 'On Hold'),
  };

  return (
    <div className="kanban-board">
      <div className="kanban-columns-container">
        {Object.entries(groupedLeads).map(([status, statusLeads]) => (
          <KanbanColumn
            key={status}
            title={status}
            status={status}
            leads={statusLeads}
            onView={onView}
            //onEdit={onEdit}
            onDelete={onDelete}
            onComment={onComment}
            onStatusUpdate={onStatusUpdate}
            onManagerAssignment={onManagerAssignment}
            onAssociateAssignment={onAssociateAssignment}
            onDrop={onDrop}
            managers={managers}
            assignedData={assignedData}
            userId={userId}
            selectedManager={selectedManager}
            selectedAssociate={selectedAssociate}
            onManagerChange={onManagerChange}
            onAssociateChange={onAssociateChange}
          />
        ))}
      </div>
    </div>
  );
};